import React, { useState } from "react";
import { Trash2 } from "lucide-react";
import { Modal, Fld, ConfirmButton } from "../ui/primitives.jsx";
import { useToast } from "../ui/Toasts.jsx";
import { todayStr } from "../lib/format.js";
import { entryCrud, employeeCrud, payrollCrud, loanCrud, blankToNull, money } from "./api.js";

/** Shared shell: error banner, delete-if-existing, cancel, save. */
function FormShell({ title, isNew, onClose, onSave, onDelete, busy, err, children, wide }) {
  return (
    <Modal
      title={title}
      onClose={onClose}
      wide={wide}
      footer={
        <>
          {!isNew && onDelete && (
            <ConfirmButton
              className="btn danger"
              disabled={busy}
              label={<><Trash2 size={15} /> Delete</>}
              confirmLabel="Click again to delete"
              onConfirm={onDelete}
            />
          )}
          <div style={{ flex: 1 }} />
          <button className="btn ghost" onClick={onClose} disabled={busy}>Cancel</button>
          <button className="btn primary" onClick={onSave} disabled={busy}>
            {busy ? "Saving…" : "Save"}
          </button>
        </>
      }
    >
      {err && <div className="notice red">{err}</div>}
      <div className="frm">{children}</div>
    </Modal>
  );
}

export function EntryForm({ rec, kind, categories, onClose }) {
  const isNew = !rec?.id;
  const theKind = rec?.kind ?? kind;
  const cats = categories.filter((c) => c.kind === theKind).map((c) => c.name);
  const [f, setF] = useState(() => ({
    date: rec?.date ?? todayStr(),
    category: rec?.category ?? cats[0] ?? "",
    source: rec?.source ?? "",
    amount: rec?.amount ?? "",
    notes: rec?.notes ?? "",
  }));
  const [err, setErr] = useState("");
  const { notify } = useToast();
  const save = entryCrud.useSave();
  const del = entryCrud.useDelete();
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  async function onSave() {
    setErr("");
    if (!f.date) return setErr("Pick a date.");
    if (money(f.amount) <= 0) return setErr("Enter an amount greater than zero.");
    try {
      await save.mutateAsync({
        id: rec?.id,
        row: {
          kind: theKind,
          date: f.date,
          category: blankToNull(f.category),
          source: blankToNull(f.source),
          amount: money(f.amount),
          notes: blankToNull(f.notes),
        },
      });
      notify(theKind === "income" ? "Income recorded." : "Expense recorded.");
      onClose();
    } catch (e) { setErr(e.message); }
  }

  return (
    <FormShell
      title={`${isNew ? "Add" : "Edit"} ${theKind}`}
      isNew={isNew}
      onClose={onClose}
      onSave={onSave}
      onDelete={async () => {
        try { await del.mutateAsync(rec.id); notify("Entry deleted."); onClose(); }
        catch (e) { setErr(e.message); }
      }}
      busy={save.isPending || del.isPending}
      err={err}
    >
      <Fld label="Date"><input type="date" value={f.date} onChange={(e) => set("date", e.target.value)} /></Fld>
      <Fld label="Amount ($)">
        <input type="number" step="0.01" value={f.amount} onChange={(e) => set("amount", e.target.value)} />
      </Fld>
      <Fld label="Category">
        <select value={f.category} onChange={(e) => set("category", e.target.value)}>
          {cats.length === 0 && <option value="">— none defined —</option>}
          {cats.map((c) => <option key={c}>{c}</option>)}
        </select>
      </Fld>
      <Fld label={theKind === "income" ? "Received from" : "Paid to"}>
        <input value={f.source} onChange={(e) => set("source", e.target.value)} placeholder="Name or business" />
      </Fld>
      <Fld full label="Notes">
        <textarea value={f.notes} onChange={(e) => set("notes", e.target.value)} />
      </Fld>
    </FormShell>
  );
}

export function EmployeeForm({ rec, onClose }) {
  const isNew = !rec?.id;
  const [f, setF] = useState(() => ({
    name: rec?.name ?? "",
    role: rec?.role ?? "",
    payRate: rec?.pay_rate ?? "",
    payType: rec?.pay_type ?? "hourly",
  }));
  const [err, setErr] = useState("");
  const { notify } = useToast();
  const save = employeeCrud.useSave();
  const del = employeeCrud.useDelete();
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  return (
    <FormShell
      title={isNew ? "Add employee" : "Edit employee"}
      isNew={isNew}
      onClose={onClose}
      busy={save.isPending || del.isPending}
      err={err}
      onSave={async () => {
        setErr("");
        if (!f.name.trim()) return setErr("Enter a name.");
        try {
          await save.mutateAsync({
            id: rec?.id,
            row: { name: f.name.trim(), role: blankToNull(f.role), pay_rate: money(f.payRate), pay_type: f.payType },
          });
          notify("Employee saved.");
          onClose();
        } catch (e) { setErr(e.message); }
      }}
      onDelete={async () => {
        try { await del.mutateAsync(rec.id); notify("Employee deleted."); onClose(); }
        catch (e) { setErr(e.message); }
      }}
    >
      <Fld label="Name"><input value={f.name} onChange={(e) => set("name", e.target.value)} /></Fld>
      <Fld label="Role"><input value={f.role} onChange={(e) => set("role", e.target.value)} placeholder="Operator, mechanic…" /></Fld>
      <Fld label="Pay rate ($)">
        <input type="number" step="0.01" value={f.payRate} onChange={(e) => set("payRate", e.target.value)} />
      </Fld>
      <Fld label="Pay type">
        <select value={f.payType} onChange={(e) => set("payType", e.target.value)}>
          <option value="hourly">Hourly</option>
          <option value="salary">Salary</option>
        </select>
      </Fld>
    </FormShell>
  );
}

export function PayrollForm({ rec, employees, onClose }) {
  const isNew = !rec?.id;
  const [f, setF] = useState(() => ({
    employeeId: rec?.employee_id ?? employees[0]?.id ?? "",
    date: rec?.date ?? todayStr(),
    amount: rec?.amount ?? "",
    notes: rec?.notes ?? "",
  }));
  const [err, setErr] = useState("");
  const { notify } = useToast();
  const save = payrollCrud.useSave();
  const del = payrollCrud.useDelete();
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  return (
    <FormShell
      title={isNew ? "Record payment" : "Edit payment"}
      isNew={isNew}
      onClose={onClose}
      busy={save.isPending || del.isPending}
      err={err}
      onSave={async () => {
        setErr("");
        if (!f.employeeId) return setErr("Pick an employee. Add one first if the list is empty.");
        if (money(f.amount) <= 0) return setErr("Enter an amount greater than zero.");
        try {
          await save.mutateAsync({
            id: rec?.id,
            row: { employee_id: f.employeeId, date: f.date, amount: money(f.amount), notes: blankToNull(f.notes) },
          });
          notify("Payroll recorded.");
          onClose();
        } catch (e) { setErr(e.message); }
      }}
      onDelete={async () => {
        try { await del.mutateAsync(rec.id); notify("Payment deleted."); onClose(); }
        catch (e) { setErr(e.message); }
      }}
    >
      <Fld label="Employee">
        <select value={f.employeeId} onChange={(e) => set("employeeId", e.target.value)}>
          <option value="">— Select —</option>
          {employees.map((e) => <option key={e.id} value={e.id}>{e.name}</option>)}
        </select>
      </Fld>
      <Fld label="Date"><input type="date" value={f.date} onChange={(e) => set("date", e.target.value)} /></Fld>
      <Fld label="Amount ($)">
        <input type="number" step="0.01" value={f.amount} onChange={(e) => set("amount", e.target.value)} />
      </Fld>
      <Fld full label="Notes"><textarea value={f.notes} onChange={(e) => set("notes", e.target.value)} /></Fld>
    </FormShell>
  );
}

export function LoanForm({ rec, onClose }) {
  const isNew = !rec?.id;
  const [f, setF] = useState(() => ({
    lender: rec?.lender ?? "",
    purpose: rec?.purpose ?? "",
    origAmount: rec?.orig_amount ?? "",
    balance: rec?.balance ?? "",
    rate: rec?.rate ?? "",
    payment: rec?.payment ?? "",
    dueDay: rec?.due_day ?? "",
    notes: rec?.notes ?? "",
  }));
  const [err, setErr] = useState("");
  const { notify } = useToast();
  const save = loanCrud.useSave();
  const del = loanCrud.useDelete();
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  return (
    <FormShell
      title={isNew ? "Add loan" : "Edit loan"}
      isNew={isNew}
      onClose={onClose}
      wide
      busy={save.isPending || del.isPending}
      err={err}
      onSave={async () => {
        setErr("");
        if (!f.lender.trim()) return setErr("Enter a lender.");
        const day = f.dueDay === "" ? null : Math.trunc(Number(f.dueDay));
        if (day !== null && (day < 1 || day > 31)) return setErr("Due day must be between 1 and 31.");
        try {
          await save.mutateAsync({
            id: rec?.id,
            row: {
              lender: f.lender.trim(),
              purpose: blankToNull(f.purpose),
              orig_amount: money(f.origAmount),
              balance: money(f.balance),
              rate: money(f.rate),
              payment: money(f.payment),
              due_day: day,
              notes: blankToNull(f.notes),
            },
          });
          notify("Loan saved.");
          onClose();
        } catch (e) { setErr(e.message); }
      }}
      onDelete={async () => {
        try { await del.mutateAsync(rec.id); notify("Loan deleted."); onClose(); }
        catch (e) { setErr(e.message); }
      }}
    >
      <Fld label="Lender"><input value={f.lender} onChange={(e) => set("lender", e.target.value)} /></Fld>
      <Fld label="Purpose"><input value={f.purpose} onChange={(e) => set("purpose", e.target.value)} placeholder="Skid steer, truck…" /></Fld>
      <Fld label="Original amount ($)">
        <input type="number" step="0.01" value={f.origAmount} onChange={(e) => set("origAmount", e.target.value)} />
      </Fld>
      <Fld label="Current balance ($)">
        <input type="number" step="0.01" value={f.balance} onChange={(e) => set("balance", e.target.value)} />
      </Fld>
      <Fld label="Rate (%)">
        <input type="number" step="0.01" value={f.rate} onChange={(e) => set("rate", e.target.value)} />
      </Fld>
      <Fld label="Monthly payment ($)">
        <input type="number" step="0.01" value={f.payment} onChange={(e) => set("payment", e.target.value)} />
      </Fld>
      <Fld label="Due day of month" hint="1–31">
        <input type="number" min="1" max="31" value={f.dueDay} onChange={(e) => set("dueDay", e.target.value)} />
      </Fld>
      <Fld full label="Notes"><textarea value={f.notes} onChange={(e) => set("notes", e.target.value)} /></Fld>
    </FormShell>
  );
}
